// pages/Home.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import BrandCard from '../components/BrandCard';
import SuccessAlert from '../components/SuccessAlert';
import SliderBar from '../components/SliderBar';
import {
  ProgressiveLoader,
  LoadingErrorBoundary,
  AnimatedLoadingBar
} from '../components/LoadingStates';
import { useNavigate } from 'react-router-dom';
import BrandDataManager from '../utils/BrandDataManager';
import BrandFilterUtils from '../utils/filterUtils';
import { useDebouncedSearch } from '../hooks/useDebounce';
import { useCacheManager } from '../hooks/useCacheManager';
import { useOperationPerformance } from '../hooks/usePerformance';
import { useMobileOptimization, useMobileComponentOptimization } from '../hooks/useMobileOptimization';
import { useLCPOptimization, useLCPMeasurement } from '../hooks/useLCPOptimization';


// Add CSS for animation and LCP optimization
const styles = `
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn {
  animation: fadeIn 0.2s ease-out forwards;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-refresh-spin {
  animation: spin 1s ease-in-out;
}

/* Critical CSS for LCP optimization */
.brand-card-priority {
  contain: layout style paint;
}
.brand-card-priority img {
  content-visibility: auto;
}

/* Ensure first brand cards have fixed dimensions to prevent layout shift */
.brand-grid > div:nth-child(-n+4) {
  min-height: 400px;
}
.brand-grid > div:nth-child(-n+4) .relative.h-48 {
  height: 12rem !important;
}
`;

function HomePage() {
  const navigate = useNavigate();
  
  // Initialize with cached data immediately
  const [brands, setBrands] = useState(() => {
    // Try to get cached data immediately on component initialization
    const cachedData = BrandDataManager.getFromLocalStorage();
    console.log('Initial load - cached data available:', !!cachedData);
    
    // If no cached data, show a message immediately
    if (!cachedData || cachedData.length === 0) {
      console.log('No cached data found, will fetch from Firebase');
    }
    
    return cachedData || [];
  });
  
  // State variables
  const [showBonusAlert, setShowBonusAlert] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchBar, setShowSearchBar] = useState(false);
  
  // Debounced search for better performance
  const { debouncedSearchTerm, isSearching } = useDebouncedSearch(searchTerm, 300);
  
  // Cache management
  const { cacheStatus, refreshStatus, forceRefresh: cacheForceRefresh } = useCacheManager();
  
  // Performance monitoring
  const { measureOperation } = useOperationPerformance();
  
  // Mobile optimizations
  const { isMobile, isLowEndDevice, optimizedSettings, shouldEnableFeature } = useMobileOptimization();
  const { componentRef, shouldSkipRender } = useMobileComponentOptimization('HomePage');
  
  // LCP optimizations
  const { preloadedCount } = useLCPOptimization(brands);
  const { lcpValue, lcpElement } = useLCPMeasurement();
  const [loadingState, setLoadingState] = useState(() => {
    // Determine initial loading state based on cached data
    const cachedData = BrandDataManager.getFromLocalStorage();
    return cachedData ? 'cached' : 'loading';
  });
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(() => {
    // Get last update time from cache
    const timestamp = localStorage.getItem('brandDataTimestamp');
    return timestamp ? new Date(parseInt(timestamp)) : null;
  });
  const [backgroundRefresh, setBackgroundRefresh] = useState(false);
  const loadMoreRef = useRef(null);

  // Load More functionality
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const INITIAL_BRAND_COUNT = 24;

  // Prevent multiple simultaneous Firebase calls
  const fetchingRef = useRef(false);

  // Optimized data fetching with performance monitoring
  const fetchBrands = useCallback(async (options = {}) => {
    const { forceRefresh = false, background = false } = options;
    
    // Prevent multiple simultaneous calls
    if (fetchingRef.current && !background) {
      console.log('Fetch already in progress, skipping');
      return brands;
    }
    
    fetchingRef.current = true;
    
    return await measureOperation('fetchBrands', async () => {
      try {
        if (background) {
          setBackgroundRefresh(true);
        } else if (forceRefresh) {
          setRefreshing(true);
          setLoadingState('loading');
          setLoadingProgress(1); // Start at 1%
          
          // Smooth progress animation over 10 seconds (1% to 95%)
          const startTime = Date.now();
          const duration = 10000; // 10 seconds
          const progressInterval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const progressPercent = Math.min(95, 1 + (elapsed / duration) * 94); // 1% to 95%
            
            setLoadingProgress(progressPercent);
            
            if (progressPercent >= 95) {
              clearInterval(progressInterval);
            }
          }, 100); // Update every 100ms for smooth animation
        }

        console.log('Fetching brands with options:', options);

        // Use the optimized BrandDataManager with timeout
        const freshBrands = await Promise.race([
          BrandDataManager.getBrands({
            forceRefresh,
            useCache: !forceRefresh
          }),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Request timeout after 15 seconds')), 15000)
          )
        ]);

        // Update state with fresh data
        setBrands(freshBrands);
        setLastUpdated(new Date());
        setError(null);
        setLoadingState('loaded');
        setLoadingProgress(100);

        console.log('Brands updated successfully:', freshBrands.length);
        return freshBrands;
      } catch (err) {
        console.error("Error fetching brands:", err);

        // Set more specific error messages
        let errorMessage = "Failed to load brands";
        if (err.message.includes('timeout')) {
          errorMessage = "Request timed out. Please check your internet connection and try again.";
        } else if (err.code === 'permission-denied') {
          errorMessage = "Permission denied - check Firebase security rules";
        } else if (err.code === 'unavailable') {
          errorMessage = "Firebase service unavailable - check connection";
        } else if (err.code === 'failed-precondition') {
          errorMessage = "Database index missing - check Firestore indexes";
        }

        setError(errorMessage);
        setLoadingState('error');
        setLoadingProgress(0);
        throw err;
      } finally {
        setRefreshing(false);
        setBackgroundRefresh(false);
        fetchingRef.current = false;
      }
    });
  }, [measureOperation, brands]);

  // Intelligent data loading strategy with immediate fallback
  useEffect(() => {
    const initializeData = async () => {
      // Always try to show cached data first, even if stale
      const cachedData = BrandDataManager.getFromLocalStorage();
      if (cachedData && cachedData.length > 0) {
        console.log('Showing cached data immediately to improve perceived performance');
        setBrands(cachedData);
        setLoadingState('cached');
        
        // Preload first 4 brand images for LCP optimization
        const firstFourBrands = cachedData.slice(0, 4);
        firstFourBrands.forEach((brand, index) => {
          if (brand.coverImage && !brand.coverImage.startsWith('data:')) {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = brand.coverImage;
            
            // Set highest priority for the first image (LCP candidate)
            if (index === 0) {
              link.fetchPriority = 'high';
            }
            
            document.head.appendChild(link);
          }
        });
        
        // Always refresh in background, but don't block UI
        setTimeout(() => {
          fetchBrands({ background: true });
        }, 100); // Small delay to let UI render first
      } else {
        console.log('No cached data, fetching initial data with timeout');
        setLoadingState('loading');
        
        // Set a maximum wait time for initial load (but don't show error to user)
        const timeoutId = setTimeout(() => {
          console.warn('Firebase taking too long, using fallback data');
          // Don't set error state, just use fallback data silently
          setLoadingState('loaded');
          setLoadingProgress(100);
        }, 12000); // 12 second timeout (2 seconds after progress completes)
        
        try {
          await fetchBrands({ forceRefresh: true });
          clearTimeout(timeoutId);
        } catch (error) {
          clearTimeout(timeoutId);
          console.error('Initial fetch failed:', error);
        }
      }
    };

    initializeData();
  }, []); // Empty dependency array for initial load only

  // Handle manual refresh
  const handleRefresh = useCallback(async () => {
    // Use both local refresh and cache manager refresh
    await Promise.all([
      fetchBrands({ forceRefresh: true }),
      cacheForceRefresh()
    ]);
  }, [fetchBrands, cacheForceRefresh]);

  // Handle Load More functionality
  const handleLoadMore = useCallback(async () => {
    setLoadingMore(true);
    setLoadingProgress(1);

    // Simulate loading progress over 3 seconds
    const startTime = Date.now();
    const duration = 3000; // 3 seconds
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progressPercent = Math.min(95, 1 + (elapsed / duration) * 94); // 1% to 95%

      setLoadingProgress(progressPercent);

      if (progressPercent >= 95) {
        clearInterval(progressInterval);
      }
    }, 50); // Update every 50ms for smooth animation

    // Wait for the loading animation
    setTimeout(() => {
      setLoadingProgress(100);
      setShowAllBrands(true);
      setLoadingMore(false);
      clearInterval(progressInterval);
    }, duration);
  }, []);

  // Create search index for better performance (skip on low-end devices if rendering too frequently)
  const indexedBrands = useMemo(() => {
    if (shouldSkipRender && isLowEndDevice) {
      return brands; // Skip expensive operations on low-end devices
    }
    
    console.log('Creating search index for brands:', brands.length);
    return BrandFilterUtils.createSearchIndex(brands);
  }, [brands, shouldSkipRender, isLowEndDevice]);

  // Optimized filtering with memoization and debounced search
  const filteredBrands = useMemo(() => {
    console.log('Filtering brands:', { 
      activeCategory, 
      searchTerm: debouncedSearchTerm, 
      totalBrands: indexedBrands.length 
    });
    
    const startTime = performance.now();
    
    const filtered = BrandFilterUtils.filterBrands(indexedBrands, {
      category: activeCategory,
      search: debouncedSearchTerm
    });

    const endTime = performance.now();
    console.log(`Filtering completed in ${(endTime - startTime).toFixed(2)}ms`);
    
    return filtered;
  }, [indexedBrands, activeCategory, debouncedSearchTerm]);

  // Get filter statistics for UI feedback
  const filterStats = useMemo(() => {
    return BrandFilterUtils.getFilterStats(indexedBrands, {
      category: activeCategory,
      search: debouncedSearchTerm
    });
  }, [indexedBrands, activeCategory, debouncedSearchTerm]);

  const handleClaimBonus = (brand) => {
    setSelectedBrand(brand);
    setShowBonusAlert(true);

    // Auto close after 5 seconds
    setTimeout(() => {
      setShowBonusAlert(false);
    }, 5000);
  };

  // Handle search toggle
  const toggleSearchBar = () => {
    setShowSearchBar(!showSearchBar);
  };



  // Handle button clicks
  const handleExploreClick = () => {
    // Scroll to the brand listings section
    const brandListingsElement = document.getElementById('brand-listings');
    if (brandListingsElement) {
      brandListingsElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleBonusComparisonClick = () => {
    // Navigate to promotions page
    navigate('/promotions');
  };

  return (
    <div className="pb-8" ref={componentRef}>
      {/* Add the animation styles - conditionally disable on low-end devices */}
      <style>{shouldEnableFeature('animations') ? styles : ''}</style>

      {/* Success Alert */}
      {showBonusAlert && selectedBrand && (
        <SuccessAlert
          brand={selectedBrand}
          onClose={() => setShowBonusAlert(false)}
        />
      )}

      {/* Hero Banner (disabled) */}
      {false && (
        <div className="relative bg-blue-900 text-white">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src="/img/brand/hero-banner.jpg"
              alt="Casino games"
              className="w-full h-full object-cover object-center opacity-40"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = 'none';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-blue-800/40"></div>
          </div>

          {/* Hero Content */}
          <div className="relative z-10 max-w-6xl mx-auto px-4 py-12 md:py-24">
            <div className="md:w-2/3 space-y-4">
              <h1 className="text-3xl md:text-5xl font-bold leading-tight">
                Apakah kamu pernah ditipu
              </h1>
              <p className="text-lg md:text-xl text-blue-100 opacity-90">
                300 situs web, bagaimana kamu memilih? Kami jamin sponsor kami dengan perlindungan ganda. Tidak bisa tarik dana dari mereka? Kami yang bayar kamu.
              </p>

              <div className="flex flex-wrap gap-3 pt-4">
                <button
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg transition-colors"
                  onClick={handleExploreClick}
                >
                  Jelajahi Kasino Terbaik
                </button>
                <button
                  className="bg-transparent hover:bg-white/10 text-white border border-white font-semibold py-3 px-6 rounded-lg transition-colors"
                  onClick={handleBonusComparisonClick}
                >
                  Perbandingan Bonus
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/10 rounded-lg p-4 text-center backdrop-blur-sm">
                <div className="text-3xl font-bold">{brands.length}+</div>
                <div className="text-blue-100 text-sm"> Merek</div>
              </div>
              <div className="bg-white/10 rounded-lg p-4 text-center backdrop-blur-sm">
                <div className="text-3xl font-bold">50,000k+</div>
                <div className="text-blue-100 text-sm">In (IDR) Bonus</div>
              </div>
              <div className="bg-white/10 rounded-lg p-4 text-center backdrop-blur-sm">
                <div className="text-3xl font-bold">24/7</div>
                <div className="text-blue-100 text-sm">Dukungan</div>
              </div>
              <div className="bg-white/10 rounded-lg p-4 text-center backdrop-blur-sm">
                <div className="text-3xl font-bold">10K+</div>
                <div className="text-blue-100 text-sm">Games</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar Overlay */}
      {showSearchBar && (
        <div className="fixed inset-x-0 top-0 bg-white shadow-md p-2 z-70 animate-fadeIn">
          <div className="relative">
            <input
              type="text"
              placeholder="Search brands, bonuses, or features..."
              className="w-full py-2 px-4 pr-10 rounded-lg text-gray-700 border border-gray-300"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
            <button
              onClick={toggleSearchBar}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              aria-label="Close search"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Home Category Slider Bar */}
      <SliderBar
        type="home"
        items={[]}
        selectedItem={activeCategory}
        onItemChange={setActiveCategory}
        onToggleSearch={toggleSearchBar}
        onRefresh={handleRefresh}
        showSearch={true}
        showRefresh={true}
        isRefreshing={refreshing || backgroundRefresh || refreshStatus.isRefreshing}
        lastUpdated={lastUpdated || refreshStatus.lastRefresh}
      >

        {/* Custom category buttons with counts */}
        <button
          className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 ${activeCategory === 'all'
            ? 'bg-blue-600 text-white'
            : 'bg-white text-gray-700 border border-gray-200 shadow-sm hover:bg-gray-50'
            }`}
          onClick={() => setActiveCategory('all')}
        >
          <span className="hidden sm:inline">All Brands</span>
          <span className="sm:hidden">All</span>
          <span className="ml-1">({filterStats.categories.all})</span>
        </button>
        <button
          className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 ${activeCategory === 'new'
            ? 'bg-blue-600 text-white'
            : 'bg-white text-gray-700 border border-gray-200 shadow-sm hover:bg-gray-50'
            }`}
          onClick={() => setActiveCategory('new')}
        >
          <span className="hidden sm:inline">New Brands</span>
          <span className="sm:hidden">New</span>
          <span className="ml-1">({filterStats.categories.new})</span>
        </button>
        <button
          className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 ${activeCategory === 'top'
            ? 'bg-blue-600 text-white'
            : 'bg-white text-gray-700 border border-gray-200 shadow-sm hover:bg-gray-50'
            }`}
          onClick={() => setActiveCategory('top')}
        >
          <span className="hidden sm:inline">Top Rated</span>
          <span className="sm:hidden">Top</span>
          <span className="ml-1">({filterStats.categories.top})</span>
        </button>
      </SliderBar>

      {/* Brand Listings - Full Width */}
      <div id="brand-listings" className="w-full px-4 py-4">




        {/* Search Results Count with loading indicator */}
        {(searchTerm || debouncedSearchTerm) && (
          <div className="flex items-center mb-4">
            <p className="text-gray-600">
              {isSearching ? (
                <>
                  <svg className="inline w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Searching...
                </>
              ) : (
                <>
                  Found {filteredBrands.length} {filteredBrands.length === 1 ? 'result' : 'results'} 
                  {debouncedSearchTerm && ` for "${debouncedSearchTerm}"`}
                  {activeCategory !== 'all' && ` in ${activeCategory} category`}
                </>
              )}
            </p>
          </div>
        )}

        <LoadingErrorBoundary error={null} onRetry={handleRefresh}>
          {loadingState === 'loading' && filteredBrands.length === 0 ? (
            <div className="py-8">
              <AnimatedLoadingBar 
                message="Loading brands..." 
                subMessage="Fetching the latest brand information from our database"
                progress={loadingProgress}
              />
            </div>
          ) : (
            <ProgressiveLoader
              loadingState={loadingState}
              hasData={filteredBrands.length > 0}
              skeletonCount={8}
              loadingMessage="Loading brands..."
              emptyMessage={
                debouncedSearchTerm 
                  ? `No results found for "${debouncedSearchTerm}". Try different keywords or browse all brands.`
                  : "No brands found in this category. Try a different category."
              }
            >
            <div className={`brand-grid grid gap-6 ${
              isMobile
                ? 'grid-cols-1 sm:grid-cols-2'
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            }`}>
              {filteredBrands
                .slice(0, showAllBrands ? filteredBrands.length : INITIAL_BRAND_COUNT)
                .map((brand, index) => (
                  <BrandCard
                    key={brand.id}
                    brand={brand}
                    onClaimBonus={handleClaimBonus}
                    priority={index < 4} // Mark first 4 images as priority for LCP optimization
                    isLCPCandidate={index === 0} // Mark first image as LCP candidate
                  />
                ))}
            </div>

            {/* Load More Button */}
            {!showAllBrands && filteredBrands.length > INITIAL_BRAND_COUNT && (
              <div className="text-center mt-8">
                {loadingMore ? (
                  <div className="py-8">
                    <AnimatedLoadingBar
                      message="Loading more brands..."
                      subMessage="Loading all remaining brands and images"
                      progress={loadingProgress}
                    />
                  </div>
                ) : (
                  <button
                    className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-lg transition-colors font-medium"
                    onClick={handleLoadMore}
                  >
                    Load More Brands ({filteredBrands.length - INITIAL_BRAND_COUNT} remaining)
                  </button>
                )}
              </div>
            )}
            

          </ProgressiveLoader>
          )}
        </LoadingErrorBoundary>
      </div>
    </div>
  );
}

export default HomePage;